import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  FileText, Plus, Search, Filter, CheckCircle, XCircle,
  Clock, Calendar, AlertTriangle, ArrowRight, User, UserCheck, Trash2, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { fullName } from '../lib/fullName';

import { API_BASE as API_URL } from '../lib/apiBase';
import { safeJson } from '../lib/safeJson';
import { CompOffValidity } from '../components/CompOffValidity';
import { LeaveRecordsReport } from '../components/LeaveRecordsReport';
// Default fallback when /api/leaves/types hasn't loaded yet (or returns nothing)
const DEFAULT_LEAVE_TYPES = [
  "Casual Leave", "Earned Leave", "Medical Leave", "Maternity Leave",
  "Paternity Leave", "Study Leave", "Academic Leave", "On-Duty", "Vacation Leave",
  "Half Day", "Compensatory Off"
];

// Current month (YYYY-MM) using the LOCAL clock, not UTC — avoids defaulting to the
// previous month during the early hours of the 1st in +05:30.
const localMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// Leave approval status mapping (Employee → HOD → HR → Principal)
const STATUS_CONFIG = {
  pending: { label: 'Pending HOD', color: 'bg-yellow-100 text-yellow-800', nextApprover: 'HOD' },
  pending_l2: { label: 'Pending HR', color: 'bg-blue-100 text-blue-800', nextApprover: 'HR' },
  pending_l3: { label: 'Pending Principal', color: 'bg-indigo-100 text-indigo-800', nextApprover: 'Principal' },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800', nextApprover: null },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800', nextApprover: null },
  cancelled: { label: 'Cancelled', color: 'bg-slate-100 text-slate-800', nextApprover: null }
};

// Role to approval level mapping (new flow: HOD→HR→Principal).
// HR / admin / branch_admin are CENTRAL authorities — they can clear a leave stuck
// at ANY stage (the backend forwards it to an active Principal, else finalises).
const ROLE_CAN_APPROVE = {
  hod: ['pending'],
  hr: ['pending', 'pending_l2', 'pending_l3'],
  admin: ['pending', 'pending_l2', 'pending_l3'],
  branch_admin: ['pending', 'pending_l2', 'pending_l3'],
  principal: ['pending_l3'],
  dean: ['pending_l3'],
  director: ['pending_l3'],
  super_admin: ['pending', 'pending_l2', 'pending_l3']
};

export default function Leaves() {
  const { selectedEntity } = useOutletContext() || {};
  const { user } = useAuth();
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [myApprovals, setMyApprovals] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [leaveTypeFilter, setLeaveTypeFilter] = useState('all');
  const [employeeFilter, setEmployeeFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [monthFilter, setMonthFilter] = useState(() => localMonth());
  const [analytics, setAnalytics] = useState(null);
  const [editingLeave, setEditingLeave] = useState(null);
  const [selectedLeaveIds, setSelectedLeaveIds] = useState([]);
  const [bulkUpdates, setBulkUpdates] = useState({ status: '', leave_type: '' });
  const [bulkSaving, setBulkSaving] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [formData, setFormData] = useState({
    employee_id: '',
    leave_type: '',
    start_date: '',
    end_date: '',
    days: 1,
    half_day: false,
    half_day_session: 'morning',
    reason: ''
  });
  const [myProfile, setMyProfile] = useState(null);
  const [myAllowedTypes, setMyAllowedTypes] = useState(null); // null = all allowed; array = filtered
  const [employeePolicyMap, setEmployeePolicyMap] = useState({}); // employee_id -> allowed_types[]
  const [balanceMap, setBalanceMap] = useState({}); // employee_id -> {leaveType: {balance, total, used}}
  const [LEAVE_TYPES, setLeaveTypes] = useState(DEFAULT_LEAVE_TYPES);

  // Dynamic leave types from /api/leaves/types — built-ins + global + entity-scoped customs
  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('hrms-token');
        const res = await fetch(`${API_URL}/api/leaves/types`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          credentials: 'include',
        });
        if (!res.ok) return;
        const data = await res.json();
        const names = (Array.isArray(data) ? data : [])
          .filter(t => t.is_active !== false)
          .map(t => t.name);
        if (names.length) setLeaveTypes(names);
      } catch {
        // keep defaults
      }
    };
    load();
  }, []);

  /** Return "5 left" or "" for a given employee + type. */
  const balanceLabelFor = (employee_id, type) => {
    if (!employee_id || !type) return '';
    const emap = balanceMap[employee_id];
    if (!emap) return '';
    const b = emap[type];
    if (!b) return '';
    const remaining = (b.balance !== undefined ? b.balance : (b.total || 0) - (b.used || 0));
    return `${remaining} left`;
  };

  const isEmployee = user?.role === 'employee';
  const canEdit = ['super_admin', 'admin', 'hr', 'branch_admin'].includes(user?.role);

  // Resolve allowed types for the currently chosen applicant
  const activeApplicantId = isEmployee ? myProfile?.employee_id : formData.employee_id;
  const allowedTypesForApplicant = activeApplicantId
    ? (employeePolicyMap[activeApplicantId] ?? myAllowedTypes ?? LEAVE_TYPES)
    : LEAVE_TYPES;
  const visibleLeaveTypes = (allowedTypesForApplicant && allowedTypesForApplicant.length > 0)
    ? allowedTypesForApplicant
    : LEAVE_TYPES;

  // Compute department list from loaded employee data
  const departmentOptions = React.useMemo(() => {
    const set = new Set();
    employees.forEach((e) => { if (e.department) set.add(e.department); });
    return Array.from(set).sort();
  }, [employees]);

  const getEmployeeDept = (empId) => {
    const e = employees.find((x) => x.employee_id === empId);
    return e?.department || '';
  };

  useEffect(() => {
    fetchData();
    if (isEmployee) fetchMyProfile();
  }, [selectedEntity, monthFilter]);

  const [approveDialog, setApproveDialog] = useState(null); // leave being approved by HR
  const [approveMode, setApproveMode] = useState('full');
  const [lwp, setLwp] = useState(false);   // final-authority LWP toggle
  const [paidDaysInput, setPaidDaysInput] = useState('');
  const [approveBalances, setApproveBalances] = useState(null);
  const [specialProvision, setSpecialProvision] = useState(false);
  const [specialRemark, setSpecialRemark] = useState('');
  const [compOffCredits, setCompOffCredits] = useState(null);
  const [signoffs, setSignoffs] = useState([]);
  const [rejectDialog, setRejectDialog] = useState(null);
  const [rejectRemark, setRejectRemark] = useState('');
  const [bulkSel, setBulkSel] = useState({});
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkReject, setBulkReject] = useState({ open: false, reason: '' });
  const selectedIds = Object.keys(bulkSel).filter(id => bulkSel[id]);

  const doBulk = async (action, comment) => {
    setBulkBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/leaves/bulk-action`, {
        method: 'POST', headers: getHeaders(), credentials: 'include',
        body: JSON.stringify({ leave_ids: selectedIds, action, comment }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || 'Bulk action failed');
      toast.success(d.message);
      (d.failed || []).forEach(f => toast.error(`${f.leave_id}: ${f.error}`));
      setBulkSel({});
      setBulkReject({ open: false, reason: '' });
      fetchData();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBulkBusy(false);
    }
  };
  // Principal / Director are the final authority — HR has already verified
  // everything, so they only get Approve or Reject (with a reason).
  const isFinalAuthority = ['principal', 'dean', 'director'].includes(user?.role);

  const openApprove = (leave) => {
    setLwp(false);
    setApproveMode('full');
    const isComp = /compensatory|comp[- ]?off/i.test(leave.leave_type || '');
    // HR / final approvers confirm eligibility & can split paid / without-pay days.
    // Comp-off always opens the dialog so even a HOD sees a lapsed-validity warning.
    if (canEdit || isComp || isFinalAuthority) {
      setApproveDialog(leave);
      setApproveMode('full');
      setPaidDaysInput('');
      setApproveBalances(null);
      setSpecialProvision(false);
      setSpecialRemark('');
      setCompOffCredits(null);
      fetch(`${API_URL}/api/leaves/balance/${leave.employee_id}`, { headers: getHeaders(), credentials: 'include' })
        .then(res => safeJson(res).then(d => { if (res.ok && Array.isArray(d)) setApproveBalances(d); }))
        .catch(() => {});
      if (isComp) {
        fetch(`${API_URL}/api/comp-off/credits?employee_id=${leave.employee_id}`, { headers: getHeaders(), credentials: 'include' })
          .then(res => safeJson(res).then(d => { if (res.ok && Array.isArray(d)) setCompOffCredits(d); }))
          .catch(() => {});
      }
    } else {
      handleApprovalAction(leave.leave_id, 'approve');
    }
  };

  const confirmApprove = async () => {
    // Principal / Dean / Director just CONFIRM — HR has already decided the paid /
    // without-pay split, so the final authority only approves (split is preserved).
    if (isFinalAuthority) {
      await handleApprovalAction(approveDialog.leave_id, 'approve', null);
      setApproveDialog(null);
      return;
    }
    const total = Number(approveDialog?.days) || 0;
    let paid = null;
    if (approveMode === 'partial') {
      paid = parseFloat(paidDaysInput);
      if (isNaN(paid) || paid < 0 || paid > total) { toast.error(`Enter paid days between 0 and ${total}`); return; }
    }
    if (specialProvision && !specialRemark.trim()) {
      toast.error('Please add a short remark for the special provision');
      return;
    }
    // Leave Without Pay overrides the paid/partial split entirely
    if (approveMode === 'lwp') {
      await handleApprovalAction(approveDialog.leave_id, 'approve', null, { lwp: true });
      setApproveDialog(null);
      return;
    }
    await handleApprovalAction(approveDialog.leave_id, 'approve', paid,
      specialProvision ? { special_provision: true, special_remark: specialRemark.trim() } : null);
    setApproveDialog(null);
  };

  const openReject = (leave) => {
    setRejectRemark('');
    setRejectDialog(leave);
  };

  const confirmReject = async () => {
    if (isFinalAuthority && !rejectRemark.trim()) {
      toast.error('Please write the reason for rejection');
      return;
    }
    await handleApprovalAction(rejectDialog.leave_id, 'reject', null,
      { comment: rejectRemark.trim() || 'Rejected by approver' });
    setRejectDialog(null);
  };

  const fetchSignoffs = async () => {
    try {
      const res = await fetch(`${API_URL}/api/leaves/monthly-signoffs${selectedEntity ? `?entity_id=${selectedEntity.entity_id}` : ''}`,
        { headers: getHeaders(), credentials: 'include' });
      if (res.ok) setSignoffs(await res.json());
    } catch { /* non-blocking */ }
  };

  const actionSignoff = async (signoffId, action) => {
    try {
      const res = await fetch(`${API_URL}/api/leaves/monthly-signoffs/${signoffId}/action`, {
        method: 'PUT', headers: getHeaders(), credentials: 'include',
        body: JSON.stringify({ action })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed');
      toast.success(d.message);
      fetchSignoffs();
    } catch (e) { toast.error(e.message); }
  };

  useEffect(() => {
    if (['principal', 'dean', 'director', 'super_admin', 'admin', 'hr', 'branch_admin'].includes(user?.role)) fetchSignoffs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntity, user?.role]);

  const getHeaders = () => {
    const token = localStorage.getItem('hrms-token');
    return { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) };
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('hrms-token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const requests = [
        fetch(`${API_URL}/api/leaves?${new URLSearchParams({
          ...(selectedEntity ? { entity_id: selectedEntity.entity_id } : {}),
          ...(monthFilter ? { month: monthFilter } : {}),
        }).toString()}`,
          { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/leaves/my-approvals`, 
          { headers, credentials: 'include' })
      ];

      // Only fetch employees list for non-employee roles (employees use their own profile)
      if (!isEmployee) {
        requests.push(
          fetch(`${API_URL}/api/employees${selectedEntity ? `?entity_id=${selectedEntity.entity_id}` : ''}`, 
            { headers, credentials: 'include' })
        );
      }

      const results = await Promise.all(requests);
      
      if (results[0].ok) setLeaveRequests(await results[0].json());
      if (results[1].ok) setMyApprovals(await results[1].json());
      if (!isEmployee && results[2]?.ok) setEmployees(await results[2].json());
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load leave data');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyProfile = async () => {
    try {
      const token = localStorage.getItem('hrms-token');
      const res = await fetch(`${API_URL}/api/me/employee-profile`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });
      if (res.ok) {
        const profile = await res.json();
        if (profile) {
          setMyProfile(profile);
          setFormData(prev => ({ ...prev, employee_id: profile.employee_id }));
          // Resolve allowed types from my leave-balance (already policy-filtered server-side)
          try {
            const balRes = await fetch(`${API_URL}/api/me/leave-balance`, {
              headers: token ? { 'Authorization': `Bearer ${token}` } : {},
              credentials: 'include'
            });
            if (balRes.ok) {
              const balances = await balRes.json();
              const types = Array.from(new Set(balances.map(b => b.leave_type)));
              if (types.length > 0) setMyAllowedTypes(types);
              // Populate balance map for the balance-next-to-type dropdown label
              const emap = {};
              balances.forEach(b => { emap[b.leave_type] = b; });
              setBalanceMap(prev => ({ ...prev, [profile.employee_id]: emap }));
            }
          } catch {}
        }
      }
    } catch (e) { console.error(e); }
  };

  const resolveAllowedTypesForEmployee = async (employee_id) => {
    if (!employee_id || employeePolicyMap[employee_id]) return;
    try {
      const token = localStorage.getItem('hrms-token');
      const res = await fetch(`${API_URL}/api/leaves/balance/${employee_id}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });
      if (res.ok) {
        const balances = await res.json();
        const types = Array.from(new Set(balances.map(b => b.leave_type)));
        setEmployeePolicyMap(prev => ({ ...prev, [employee_id]: types }));
        const emap = {};
        balances.forEach(b => { emap[b.leave_type] = b; });
        setBalanceMap(prev => ({ ...prev, [employee_id]: emap }));
      }
    } catch {}
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('hrms-token');
      const employee = isEmployee ? myProfile : employees.find(emp => emp.employee_id === formData.employee_id);
      
      const response = await fetch(`${API_URL}/api/leaves`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          ...formData,
          entity_id: employee?.entity_id || selectedEntity?.entity_id
        }),
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Leave request submitted successfully!');
        setIsDialogOpen(false);
        setFormData({
          employee_id: '',
          leave_type: '',
          start_date: '',
          end_date: '',
          days: 1,
          half_day: false,
          half_day_session: 'morning',
          reason: ''
        });
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit leave request');
      }
    } catch (error) {
      toast.error('Failed to submit leave request');
    }
  };

  const handleApprovalAction = async (leaveId, action, paidDays = null, extra = null) => {
    try {
      const token = localStorage.getItem('hrms-token');
      const response = await fetch(`${API_URL}/api/leaves/${leaveId}/action`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ action, comment: action === 'reject' ? 'Rejected by approver' : null, paid_days: paidDays, ...(extra || {}) }),
        credentials: 'include'
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || `Leave ${action}d successfully`);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || `Failed to ${action} leave`);
      }
    } catch (error) {
      toast.error(`Failed to ${action} leave`);
    }
  };

  const calculateDays = (start, end) => {
    if (!start || !end) return 0;
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate - startDate);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  const handleDeleteLeave = async (leaveId) => {
    if (!window.confirm('Are you sure you want to delete this leave request?')) return;
    try {
      const token = localStorage.getItem('hrms-token');
      const res = await fetch(`${API_URL}/api/leaves/${leaveId}`, {
        method: 'DELETE',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Delete failed'); }
      toast.success('Leave request deleted');
      fetchData();
    } catch (e) { toast.error(e.message); }
  };

  const getEmployeeName = (employeeId, storedName = '') => {
    if (isEmployee && myProfile && myProfile.employee_id === employeeId) {
      return fullName(myProfile);
    }
    const emp = employees.find(e => e.employee_id === employeeId);
    if (emp) return fullName(emp);
    if (storedName && storedName.trim()) return storedName.trim();
    return employeeId || 'Unknown Employee';
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || { label: status, color: 'bg-slate-100 text-slate-800' };
    return config;
  };

  const canUserApprove = (leaveStatus) => {
    if (!user?.role) return false;
    const allowedStatuses = ROLE_CAN_APPROVE[user.role] || [];
    return allowedStatuses.includes(leaveStatus);
  };

  const filteredRequests = leaveRequests.filter(leave => {
    const empName = getEmployeeName(leave.employee_id, leave.employee_name).toLowerCase();
    const matchesSearch = empName.includes(searchTerm.toLowerCase()) || 
                          (leave.employee_id || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || leave.status === statusFilter;
    const matchesDept = departmentFilter === 'all' || getEmployeeDept(leave.employee_id) === departmentFilter;
    const matchesType = leaveTypeFilter === 'all' || leave.leave_type === leaveTypeFilter;
    const matchesEmployee = employeeFilter === 'all' || leave.employee_id === employeeFilter;
    let matchesDate = true;
    if (dateRange.start && dateRange.end) {
      matchesDate = (leave.start_date <= dateRange.end) && (leave.end_date >= dateRange.start);
    }
    return matchesSearch && matchesStatus && matchesDept && matchesType && matchesEmployee && matchesDate;
  });

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('hrms-token');
      const params = new URLSearchParams();
      if (dateRange.start && dateRange.end) {
        params.set('start_date', dateRange.start);
        params.set('end_date', dateRange.end);
      }
      if (departmentFilter !== 'all') params.set('department', departmentFilter);
      if (employeeFilter !== 'all') params.set('employee_id', employeeFilter);
      if (selectedEntity?.entity_id) params.set('entity_id', selectedEntity.entity_id);
      const res = await fetch(`${API_URL}/api/leaves/analytics?${params.toString()}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });
      if (res.ok) setAnalytics(await res.json());
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    if (activeTab === 'analytics') fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, dateRange, departmentFilter, employeeFilter, selectedEntity]);

  const handleEditLeave = async (e) => {
    e.preventDefault();
    if (!editingLeave) return;
    try {
      const token = localStorage.getItem('hrms-token');
      const res = await fetch(`${API_URL}/api/leaves/${editingLeave.leave_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({
          start_date: editingLeave.start_date,
          end_date: editingLeave.end_date,
          leave_type: editingLeave.leave_type,
          reason: editingLeave.reason,
          status: editingLeave.status,
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Update failed');
      toast.success('Leave record updated');
      setEditingLeave(null);
      fetchData();
    } catch (err) { toast.error(err.message); }
  };

  const handleBulkLeaveUpdate = async (e) => {
    e?.preventDefault();
    if (selectedLeaveIds.length === 0) {
      toast.error('Select at least one leave record');
      return;
    }
    const updates = {};
    if (bulkUpdates.status) updates.status = bulkUpdates.status;
    if (bulkUpdates.leave_type) updates.leave_type = bulkUpdates.leave_type;
    if (Object.keys(updates).length === 0) {
      toast.error('Choose at least one field to update');
      return;
    }
    setBulkSaving(true);
    try {
      const token = localStorage.getItem('hrms-token');
      const res = await fetch(`${API_URL}/api/leaves/bulk-update`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        credentials: 'include',
        body: JSON.stringify({ leave_ids: selectedLeaveIds, updates })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Bulk update failed');
      toast.success(`Updated ${data.modified} of ${selectedLeaveIds.length} records`);
      setSelectedLeaveIds([]);
      setBulkUpdates({ status: '', leave_type: '' });
      fetchData();
    } catch (err) { toast.error(err.message); }
    finally { setBulkSaving(false); }
  };

  const toggleLeaveSelection = (leaveId) => {
    setSelectedLeaveIds(prev => prev.includes(leaveId) ? prev.filter(id => id !== leaveId) : [...prev, leaveId]);
  };
  const handleBulkLeaveDelete = async () => {
    if (selectedLeaveIds.length === 0) { toast.error('Select leave records first'); return; }
    if (!window.confirm(`Delete ${selectedLeaveIds.length} leave record(s)? Approved leaves will restore the employee's balance. This cannot be undone.`)) return;
    setBulkSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/leaves/bulk-delete`, {
        method: 'POST', headers: getHeaders(), credentials: 'include',
        body: JSON.stringify({ leave_ids: selectedLeaveIds })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Bulk delete failed');
      toast.success(data.message);
      setSelectedLeaveIds([]);
      fetchData();
    } catch (e) { toast.error(e.message); }
    finally { setBulkSaving(false); }
  };

  const handleDeleteAllLeaves = async () => {
    const scope = selectedEntity ? selectedEntity.name : 'ALL institutes';
    if (!window.confirm(`Delete ALL leave records for ${scope}? Approved leaves will restore balances. This cannot be undone.`)) return;
    if (!window.confirm('Are you absolutely sure? Every leave application record will be permanently removed.')) return;
    setBulkSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/leaves/bulk-delete`, {
        method: 'POST', headers: getHeaders(), credentials: 'include',
        body: JSON.stringify({ all: true, entity_id: selectedEntity?.entity_id || null })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success(data.message);
      setSelectedLeaveIds([]);
      fetchData();
    } catch (e) { toast.error(e.message); }
    finally { setBulkSaving(false); }
  };

  const toggleAllVisible = () => {
    const visibleIds = filteredRequests.map(l => l.leave_id);
    if (visibleIds.every(id => selectedLeaveIds.includes(id))) {
      setSelectedLeaveIds(prev => prev.filter(id => !visibleIds.includes(id)));
    } else {
      setSelectedLeaveIds(prev => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  const pendingCount = leaveRequests.filter(l => ['pending', 'pending_l2', 'pending_l3'].includes(l.status)).length;
  const approvedCount = leaveRequests.filter(l => l.status === 'approved').length;

  const renderApprovalHistory = (leave) => {
    if (!leave.approval_history || leave.approval_history.length === 0) {
      return (
        <div className="text-sm text-slate-500 italic">
          No approval actions yet
        </div>
      );
    }
    
    return (
      <div className="space-y-2">
        {leave.approval_history.map((action, idx) => (
          <div key={idx} className="flex items-center gap-2 text-sm">
            <Badge variant="outline" className={action.action === 'approve' ? 'border-green-500 text-green-600' : 'border-red-500 text-red-600'}>
              {action.level}
            </Badge>
            <span className="text-slate-600">
              {action.action === 'approve' ? '✓' : '✗'} by {action.user_name || action.role}
            </span>
            <span className="text-slate-400 text-xs">
              {new Date(action.timestamp).toLocaleDateString()}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const renderApprovalFlow = (status) => {
    const steps = [
      { label: 'HOD', level: 'L1', status: 'pending' },
      { label: 'HR', level: 'L2', status: 'pending_l2' },
      { label: 'Principal', level: 'L3', status: 'pending_l3' },
      { label: 'Done', level: 'Complete', status: 'approved' }
    ];
    
    const currentIndex = steps.findIndex(s => s.status === status);
    
    return (
      <div className="flex items-center gap-1 text-xs">
        {steps.map((step, idx) => (
          <React.Fragment key={step.level}>
            <div className={`px-2 py-1 rounded ${
              idx < currentIndex ? 'bg-green-100 text-green-700' :
              idx === currentIndex ? 'bg-yellow-100 text-yellow-700 font-semibold' :
              'bg-slate-100 text-slate-400'
            }`}>
              {step.label}
            </div>
            {idx < steps.length - 1 && (
              <ArrowRight className={`w-3 h-3 ${idx < currentIndex ? 'text-green-500' : 'text-slate-300'}`} />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-4 md:space-y-6 animate-fade-in" data-testid="leaves-page">
      {/* Comp-off expiry visibility — any user with comp-off balance/credits */}
      <CompOffValidity />
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-slate-900 dark:text-white">
            Leave Management
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Process and track leave requests
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary w-full md:w-auto" data-testid="apply-leave-btn">
              <Plus className="w-4 h-4 mr-2" />
              Apply Leave
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85dvh] overflow-y-auto pb-6">
            <DialogHeader>
              <DialogTitle>Apply for Leave</DialogTitle>
              <p className="text-sm text-slate-500">Submit a new leave request for approval</p>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Employee</Label>
                {isEmployee ? (
                  <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg border" data-testid="employee-self-display">
                    <User className="w-4 h-4 text-slate-500" />
                    <span className="font-medium text-slate-800">
                      {myProfile ? `${myProfile.first_name} ${myProfile.last_name}` : 'Loading...'}
                    </span>
                    {myProfile && <Badge variant="outline" className="ml-auto text-xs">{myProfile.department}</Badge>}
                  </div>
                ) : (
                <Select
                  value={formData.employee_id}
                  onValueChange={(value) => { setFormData({ ...formData, employee_id: value, leave_type: '' }); resolveAllowedTypesForEmployee(value); }}
                >
                  <SelectTrigger data-testid="select-employee">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map(emp => (
                      <SelectItem key={emp.employee_id} value={emp.employee_id}>
                        {emp.first_name} {emp.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                )}
              </div>
              <div className="space-y-2">
                <Label>Leave Type</Label>
                <Select
                  value={formData.leave_type}
                  onValueChange={(value) => {
                    const isHalf = value === 'Half Day';
                    setFormData({
                      ...formData,
                      leave_type: value,
                      half_day: isHalf,
                      days: isHalf ? 0.5 : calculateDays(formData.start_date, formData.end_date) || 1,
                    });
                  }}
                >
                  <SelectTrigger data-testid="select-leave-type">
                    <SelectValue placeholder="Select leave type" />
                  </SelectTrigger>
                  <SelectContent>
                    {visibleLeaveTypes.map(type => {
                      const label = balanceLabelFor(activeApplicantId, type);
                      return (
                        <SelectItem key={type} value={type} data-testid={`leave-type-option-${type.replace(/\s+/g, '-')}`}>
                          <span className="flex items-center justify-between w-full gap-3">
                            <span>{type}</span>
                            {label && (
                              <span className="text-[11px] font-mono text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded" data-testid="leave-type-balance">
                                {label}
                              </span>
                            )}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {/* Show summary of remaining balance for the chosen type below the selector */}
                {formData.leave_type && balanceLabelFor(activeApplicantId, formData.leave_type) && (
                  <p className="text-xs text-emerald-700" data-testid="selected-type-balance">
                    Balance for <strong>{formData.leave_type}</strong>: {balanceLabelFor(activeApplicantId, formData.leave_type)}
                  </p>
                )}
                {visibleLeaveTypes.length < LEAVE_TYPES.length && (
                  <p className="text-[11px] text-slate-400">Only leave types in this employee&apos;s policy are shown.</p>
                )}
                {formData.leave_type && formData.leave_type !== 'Half Day' && (
                  <label className="flex items-center gap-2 text-sm mt-1 cursor-pointer">
                    <input type="checkbox" checked={formData.half_day}
                      onChange={(e) => setFormData({
                        ...formData,
                        half_day: e.target.checked,
                        days: e.target.checked ? 0.5 : (calculateDays(formData.start_date, formData.end_date) || 1),
                        end_date: e.target.checked ? formData.start_date : formData.end_date,
                      })}
                      data-testid="apply-half-day-checkbox" />
                    Apply as Half Day (0.5 day)
                  </label>
                )}
              </div>

              {/* Half-day session selector — shown for any half-day application */}
              {formData.half_day && (
                <div className="space-y-2" data-testid="half-day-controls">
                  <Label>Half-Day Session</Label>
                  <Select
                    value={formData.half_day_session}
                    onValueChange={(v) => setFormData({ ...formData, half_day_session: v })}
                  >
                    <SelectTrigger data-testid="half-day-session"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="morning">Morning (First Half)</SelectItem>
                      <SelectItem value="afternoon">Afternoon (Second Half)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{formData.half_day ? 'Date' : 'Start Date'}</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => {
                      const start = e.target.value;
                      if (formData.half_day) {
                        setFormData({ ...formData, start_date: start, end_date: start, days: 0.5 });
                      } else {
                        setFormData({
                          ...formData,
                          start_date: start,
                          days: calculateDays(start, formData.end_date)
                        });
                      }
                    }}
                    data-testid="leave-start-date"
                  />
                </div>
                {!formData.half_day && (
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => {
                      const end = e.target.value;
                      setFormData({ 
                        ...formData, 
                        end_date: end,
                        days: calculateDays(formData.start_date, end)
                      });
                    }}
                    data-testid="leave-end-date"
                  />
                </div>
                )}
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Calendar className="w-4 h-4" />
                <span>Total Days: <strong>{formData.days}</strong></span>
              </div>
              <div className="space-y-2">
                <Label>Reason</Label>
                <Textarea
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  placeholder="Enter reason for leave..."
                  rows={3}
                  data-testid="leave-reason"
                />
              </div>
              <div className="sticky bottom-0 left-0 right-0 -mx-6 -mb-6 px-6 py-3 bg-white dark:bg-slate-900 border-t flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary" disabled={!formData.employee_id} data-testid="submit-leave-btn">
                  Submit Request
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
        <Card className="stat-card">
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-slate-500">Total Requests</p>
                <p className="text-2xl font-bold">{leaveRequests.length}</p>
              </div>
              <FileText className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pending</p>
                <p className="text-2xl font-bold text-yellow-600">{pendingCount}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Approved</p>
                <p className="text-2xl font-bold text-green-600">{approvedCount}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card border-2 border-[#DC2626]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">My Approvals</p>
                <p className="text-2xl font-bold text-[#DC2626]">{myApprovals.length}</p>
              </div>
              <UserCheck className="w-8 h-8 text-[#DC2626]" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for different views */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className={`grid w-full ${isEmployee ? 'grid-cols-1' : 'grid-cols-4'} mb-4`}>
          <TabsTrigger value="all" data-testid="tab-all-leaves">
            {isEmployee ? 'My Leave Requests' : 'All Leave Requests'}
          </TabsTrigger>
          {!isEmployee && (
            <TabsTrigger value="pending" data-testid="tab-pending-approvals">
              Pending My Approval ({myApprovals.length})
            </TabsTrigger>
          )}
          {!isEmployee && (
            <TabsTrigger value="approved" data-testid="tab-approved-leaves">
              Leave Records ({approvedCount})
            </TabsTrigger>
          )}
          {!isEmployee && (
            <TabsTrigger value="analytics" data-testid="tab-leave-analytics">
              Reports & Analytics
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="pending">
          {/* Monthly consolidated leave sign-offs (Principal / Director approve the whole month's set) */}
          {signoffs.length > 0 && (
            <Card className="mb-4 border-indigo-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <UserCheck className="w-4 h-4 text-indigo-600" />Monthly Leave Sign-offs
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {signoffs.map(s => (
                  <div key={s.signoff_id} className="flex flex-wrap items-center gap-3 p-2.5 rounded-lg border bg-indigo-50/50" data-testid={`signoff-${s.signoff_id}`}>
                    <div className="flex-1 min-w-[200px]">
                      <p className="text-sm font-medium">{s.month} — {s.total_leaves} approved leave(s), {s.total_days} day(s)</p>
                      <p className="text-xs text-slate-500">Paid {s.paid_days} · Without pay {s.unpaid_days} · Locked by {s.locked_by_name || 'HR'}</p>
                    </div>
                    {s.status === 'pending' && ['principal', 'dean', 'director', 'super_admin', 'admin'].includes(user?.role) ? (
                      <div className="flex gap-2">
                        <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => actionSignoff(s.signoff_id, 'approve')} data-testid={`signoff-approve-${s.signoff_id}`}>Approve Set</Button>
                        <Button size="sm" variant="outline" className="h-7 border-red-400 text-red-600" onClick={() => actionSignoff(s.signoff_id, 'reject')}>Reject</Button>
                      </div>
                    ) : (
                      <Badge variant="outline" className={s.status === 'approved' ? 'border-emerald-500 text-emerald-600' : s.status === 'rejected' ? 'border-red-500 text-red-600' : 'border-amber-500 text-amber-600'}>
                        {s.status}
                      </Badge>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-[#DC2626]" />
                Leaves Pending Your Approval
              </CardTitle>
            </CardHeader>
            <CardContent>
              {myApprovals.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 mx-auto text-green-300 mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 dark:text-white">All caught up!</h3>
                  <p className="text-slate-500">No leave requests pending your approval</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Bulk actions — pick a whole leave type at once */}
                  <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-2" data-testid="bulk-approval-bar">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs font-medium text-slate-500">Select all of type:</span>
                      {Array.from(new Set(myApprovals.map(l => l.leave_type))).map(type => {
                        const ids = myApprovals.filter(l => l.leave_type === type).map(l => l.leave_id);
                        const allOn = ids.every(id => bulkSel[id]);
                        return (
                          <Button key={type} size="sm" variant={allOn ? 'default' : 'outline'}
                            onClick={() => setBulkSel(prev => {
                              const m = { ...prev };
                              ids.forEach(id => { m[id] = !allOn; });
                              return m;
                            })}
                            data-testid={`bulk-type-${type.replace(/\s+/g, '-').toLowerCase()}`}>
                            {type} ({ids.length})
                          </Button>
                        );
                      })}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium" data-testid="bulk-selected-count">
                        {selectedIds.length} selected
                      </span>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        disabled={selectedIds.length === 0 || bulkBusy}
                        onClick={() => doBulk('approve')} data-testid="bulk-approve-btn">
                        {bulkBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1.5" />}
                        Approve Selected
                      </Button>
                      <Button size="sm" variant="outline" className="border-red-500 text-red-600 hover:bg-red-50"
                        disabled={selectedIds.length === 0 || bulkBusy}
                        onClick={() => setBulkReject({ open: true, reason: '' })} data-testid="bulk-reject-btn">
                        <XCircle className="w-4 h-4 mr-1.5" /> Reject Selected
                      </Button>
                      {selectedIds.length > 0 && (
                        <Button size="sm" variant="ghost" onClick={() => setBulkSel({})} data-testid="bulk-clear-btn">Clear</Button>
                      )}
                    </div>
                  </div>
                  {myApprovals.map(leave => (
                    <div key={leave.leave_id} className="border rounded-lg p-4 bg-slate-50 dark:bg-slate-800" data-testid={`approval-card-${leave.leave_id}`}>
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <input type="checkbox" className="w-4 h-4 accent-[#DC2626]"
                              checked={!!bulkSel[leave.leave_id]}
                              onChange={(e) => setBulkSel(prev => ({ ...prev, [leave.leave_id]: e.target.checked }))}
                              data-testid={`bulk-check-${leave.leave_id}`} />
                            <User className="w-4 h-4 text-slate-500" />
                            <span className="font-semibold">{getEmployeeName(leave.employee_id, leave.employee_name)}</span>
                            {leave.lwp && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-semibold" data-testid={`lwp-chip-${leave.leave_id}`}>LWP</span>
                            )}
                            <Badge variant="outline">{leave.leave_type}</Badge>
                            {STATUS_CONFIG[leave.status] && (
                              <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${STATUS_CONFIG[leave.status].color}`} data-testid={`stage-chip-${leave.leave_id}`}>
                                Waiting at {STATUS_CONFIG[leave.status].nextApprover || '—'}
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-slate-600">
                            <span>{new Date(leave.start_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                            <span className="mx-2">→</span>
                            <span>{new Date(leave.end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                            <span className="ml-2 font-medium">({leave.days} days)</span>
                          </div>
                          <div className="text-sm text-slate-500">
                            <strong>Reason:</strong> {leave.reason}
                          </div>
                          <div className="mt-2">
                            {renderApprovalFlow(leave.status)}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            className="bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => openApprove(leave)}
                            data-testid={`approve-${leave.leave_id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Approve
                          </Button>
                          <Button
                            variant="outline"
                            className="border-red-500 text-red-600 hover:bg-red-50"
                            onClick={() => openReject(leave)}
                            data-testid={`reject-${leave.leave_id}`}
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="all">
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#DC2626]" />
                  All Leave Requests
                </CardTitle>
                <div className="flex flex-wrap gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search..."
                      className="pl-10 w-[180px]"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      data-testid="leave-search"
                    />
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[150px]" data-testid="leave-status-filter">
                      <Filter className="w-4 h-4 mr-2" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending HOD</SelectItem>
                      <SelectItem value="pending_l2">Pending HR</SelectItem>
                      <SelectItem value="pending_l3">Pending Principal</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                  {!isEmployee && departmentOptions.length > 0 && (
                    <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                      <SelectTrigger className="w-[160px]" data-testid="leave-department-filter">
                        <SelectValue placeholder="All Departments" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Departments</SelectItem>
                        {departmentOptions.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                  <Select value={leaveTypeFilter} onValueChange={setLeaveTypeFilter}>
                    <SelectTrigger className="w-[150px]" data-testid="leave-type-filter">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {LEAVE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-2 py-1" data-testid="leave-month-box">
                    <Input type="month" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)} className="w-[150px] h-8 border-0 bg-transparent px-1 text-sm" data-testid="leave-month-filter" />
                    <Button size="sm" variant={monthFilter ? 'outline' : 'default'} className="h-7 text-xs"
                      onClick={() => setMonthFilter(monthFilter ? '' : localMonth())}
                      data-testid="leave-month-toggle">
                      {monthFilter ? 'Show all' : 'This month'}
                    </Button>
                  </div>
                  <Input type="date" value={dateRange.start} onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })} className="w-[150px]" data-testid="leave-date-start" />
                  <Input type="date" value={dateRange.end} onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })} className="w-[150px]" data-testid="leave-date-end" />
                  {canEdit && (
                    <Button variant="outline" className="border-red-500 text-red-600 hover:bg-red-50" onClick={handleDeleteAllLeaves} data-testid="delete-all-leaves-btn">
                      <Trash2 className="w-4 h-4 mr-1.5" />Delete All
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Bulk Update Toolbar - HR / Super Admin only */}
              {canEdit && selectedLeaveIds.length > 0 && (
                <div className="mb-4 p-3 rounded-lg border-2 border-[#DC2626] bg-red-50 dark:bg-red-950/20 flex flex-col md:flex-row items-stretch md:items-center gap-3" data-testid="bulk-leave-toolbar">
                  <span className="text-sm font-semibold text-[#DC2626]">{selectedLeaveIds.length} selected</span>
                  <Select value={bulkUpdates.status} onValueChange={(v) => setBulkUpdates({ ...bulkUpdates, status: v })}>
                    <SelectTrigger className="w-[180px]" data-testid="bulk-status-select"><SelectValue placeholder="Set Status…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending HOD</SelectItem>
                      <SelectItem value="pending_l2">Pending HR</SelectItem>
                      <SelectItem value="pending_l3">Pending Principal</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={bulkUpdates.leave_type} onValueChange={(v) => setBulkUpdates({ ...bulkUpdates, leave_type: v })}>
                    <SelectTrigger className="w-[180px]" data-testid="bulk-type-select"><SelectValue placeholder="Set Leave Type…" /></SelectTrigger>
                    <SelectContent>{LEAVE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button onClick={handleBulkLeaveUpdate} disabled={bulkSaving} className="bg-[#DC2626] hover:bg-[#B91C1C] text-white" data-testid="bulk-apply-btn">
                    {bulkSaving ? 'Applying…' : 'Apply to Selected'}
                  </Button>
                  <Button variant="outline" onClick={handleBulkLeaveDelete} disabled={bulkSaving} className="border-red-500 text-red-600 hover:bg-red-50" data-testid="bulk-delete-leaves-btn">
                    <Trash2 className="w-4 h-4 mr-1.5" />Delete Selected
                  </Button>
                  <Button variant="outline" onClick={() => setSelectedLeaveIds([])} data-testid="bulk-clear-btn">Clear</Button>
                </div>
              )}
              {loading ? (
                <div className="space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="h-16 bg-slate-100 dark:bg-slate-800 rounded animate-pulse" />
                  ))}
                </div>
              ) : filteredRequests.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 dark:text-white">No leave requests</h3>
                  <p className="text-slate-500">No leave requests found</p>
                </div>
              ) : (
                <div className="overflow-x-auto responsive-card-table">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {canEdit && (
                          <TableHead className="w-10">
                            <input
                              type="checkbox"
                              checked={filteredRequests.length > 0 && filteredRequests.every(l => selectedLeaveIds.includes(l.leave_id))}
                              onChange={toggleAllVisible}
                              data-testid="bulk-leave-select-all"
                              className="w-4 h-4 accent-[#DC2626]"
                            />
                          </TableHead>
                        )}
                        <TableHead>Employee</TableHead>
                        <TableHead>Leave Type</TableHead>
                        <TableHead>Duration</TableHead>
                        <TableHead>Days</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead>Approval Flow</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredRequests.map(leave => {
                        const statusConfig = getStatusBadge(leave.status);
                        const canApprove = canUserApprove(leave.status);
                        
                        return (
                          <TableRow key={leave.leave_id} data-testid={`leave-row-${leave.leave_id}`}>
                            {canEdit && (
                              <TableCell>
                                <input
                                  type="checkbox"
                                  checked={selectedLeaveIds.includes(leave.leave_id)}
                                  onChange={() => toggleLeaveSelection(leave.leave_id)}
                                  data-testid={`select-leave-${leave.leave_id}`}
                                  className="w-4 h-4 accent-[#DC2626]"
                                />
                              </TableCell>
                            )}
                            <TableCell>
                              <div>
                                <p className="font-medium">{getEmployeeName(leave.employee_id, leave.employee_name)}</p>
                                <code className="text-xs text-slate-500">{leave.employee_id || '—'}</code>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{leave.leave_type}</Badge>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                <p>{new Date(leave.start_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</p>
                                <p className="text-slate-500">to {new Date(leave.end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="font-semibold">{leave.days}</span>
                            </TableCell>
                            <TableCell>
                              <p className="max-w-[200px] truncate text-sm text-slate-600">
                                {leave.reason}
                              </p>
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                <Badge className={statusConfig.color}>
                                  {statusConfig.label}
                                </Badge>
                                {leave.status !== 'approved' && leave.status !== 'rejected' && (
                                  <div className="mt-1">
                                    {renderApprovalFlow(leave.status)}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                {canApprove && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-green-600 hover:bg-green-50"
                                      onClick={() => openApprove(leave)}
                                      data-testid={`approve-leave-${leave.leave_id}`}
                                    >
                                      <CheckCircle className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-red-600 hover:bg-red-50"
                                      onClick={() => openReject(leave)}
                                      data-testid={`reject-leave-${leave.leave_id}`}
                                    >
                                      <XCircle className="w-4 h-4" />
                                    </Button>
                                  </>
                                )}
                                {canEdit && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-blue-600 hover:bg-blue-50"
                                    onClick={() => setEditingLeave({ ...leave })}
                                    data-testid={`edit-leave-${leave.leave_id}`}
                                    title="Edit leave record"
                                  >
                                    <FileText className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-slate-400 hover:text-red-600 hover:bg-red-50"
                                  onClick={() => handleDeleteLeave(leave.leave_id)}
                                  data-testid={`delete-leave-${leave.leave_id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Leave Records report — filterable & printable */}
        {!isEmployee && (
        <TabsContent value="approved">
          <LeaveRecordsReport
            entityId={selectedEntity?.entity_id}
            canEdit={canEdit}
            onEdit={(leave) => setEditingLeave({ ...leave })}
            onDelete={handleDeleteLeave}
            refreshKey={leaveRequests.length}
          />
        </TabsContent>
        )}

        {/* Reports & Analytics Tab */}
        {!isEmployee && (
        <TabsContent value="analytics">
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#DC2626]" />
                  Leave Reports & Analytics
                </CardTitle>
                <div className="flex flex-wrap gap-2">
                  <Input type="date" value={dateRange.start} onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })} className="w-[150px]" data-testid="analytics-date-start" />
                  <Input type="date" value={dateRange.end} onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })} className="w-[150px]" data-testid="analytics-date-end" />
                  {departmentOptions.length > 0 && (
                    <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                      <SelectTrigger className="w-[160px]" data-testid="analytics-dept-filter"><SelectValue placeholder="All Departments" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Departments</SelectItem>
                        {departmentOptions.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                  {employees.length > 0 && (
                    <Select value={employeeFilter} onValueChange={setEmployeeFilter}>
                      <SelectTrigger className="w-[200px]" data-testid="analytics-employee-filter"><SelectValue placeholder="All Staff" /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        <SelectItem value="all">All Staff</SelectItem>
                        {employees
                          .filter(e => departmentFilter === 'all' || e.department === departmentFilter)
                          .map(e => (
                            <SelectItem key={e.employee_id} value={e.employee_id}>
                              {e.full_name || e.name} {e.employee_id ? `(${e.employee_id})` : ''}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  )}
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => {
                      const today = new Date();
                      const start = new Date(today.getFullYear(), today.getMonth(), 1);
                      setDateRange({ start: start.toISOString().slice(0,10), end: today.toISOString().slice(0,10) });
                    }} data-testid="preset-this-month">This Month</Button>
                    <Button size="sm" variant="outline" onClick={() => {
                      const today = new Date();
                      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                      const end = new Date(today.getFullYear(), today.getMonth(), 0);
                      setDateRange({ start: start.toISOString().slice(0,10), end: end.toISOString().slice(0,10) });
                    }} data-testid="preset-last-month">Last Month</Button>
                    <Button size="sm" variant="outline" onClick={() => {
                      const today = new Date();
                      const start = new Date(today.getFullYear(), 0, 1);
                      setDateRange({ start: start.toISOString().slice(0,10), end: today.toISOString().slice(0,10) });
                    }} data-testid="preset-this-year">This Year</Button>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {!analytics ? (
                <p className="text-sm text-slate-400 py-6 text-center">Loading analytics…</p>
              ) : analytics.total === 0 ? (
                <p className="text-sm text-slate-400 py-6 text-center">No leave records match the selected filters.</p>
              ) : (
                <div className="space-y-6">
                  {employeeFilter !== 'all' && (
                    <div className="rounded-md border border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-950 p-2 text-xs text-blue-900 dark:text-blue-100 flex items-center justify-between" data-testid="analytics-staff-banner">
                      <span>
                        Showing analytics for{' '}
                        <strong>{(() => {
                          const e = employees.find(x => x.employee_id === employeeFilter);
                          return e ? `${e.full_name || e.name} (${e.employee_id})` : employeeFilter;
                        })()}</strong>
                      </span>
                      <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => setEmployeeFilter('all')} data-testid="analytics-clear-staff">Clear</Button>
                    </div>
                  )}
                  {/* KPI cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Total Requests</p><p className="text-2xl font-bold text-slate-900 dark:text-white">{analytics.total}</p></CardContent></Card>
                    <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Approved Days</p><p className="text-2xl font-bold text-green-600">{analytics.approved_days}</p></CardContent></Card>
                    <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Pending</p><p className="text-2xl font-bold text-amber-600">{(analytics.by_status.pending || 0) + (analytics.by_status.pending_l2 || 0) + (analytics.by_status.pending_l3 || 0)}</p></CardContent></Card>
                    <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Rejected</p><p className="text-2xl font-bold text-red-600">{analytics.by_status.rejected || 0}</p></CardContent></Card>
                  </div>

                  {/* By Department */}
                  {analytics.by_department.length > 0 && (
                    <Card>
                      <CardHeader className="pb-2"><CardTitle className="text-sm">By Department</CardTitle></CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {analytics.by_department.map((row) => {
                            const pct = analytics.total ? Math.round((row.count / analytics.total) * 100) : 0;
                            return (
                              <div key={row.department} className="flex items-center gap-3">
                                <span className="text-sm w-32 truncate">{row.department}</span>
                                <div className="flex-1 bg-slate-100 dark:bg-slate-800 rounded-full h-2 overflow-hidden">
                                  <div className="h-2 bg-[#DC2626]" style={{ width: `${pct}%` }} />
                                </div>
                                <span className="text-sm font-semibold w-12 text-right">{row.count}</span>
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* By Leave Type */}
                  {Object.keys(analytics.by_type).length > 0 && (
                    <Card>
                      <CardHeader className="pb-2"><CardTitle className="text-sm">By Leave Type</CardTitle></CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {Object.entries(analytics.by_type).map(([k, v]) => (
                            <div key={k} className="flex justify-between p-2 rounded bg-slate-50 dark:bg-slate-800">
                              <span className="text-sm">{k}</span>
                              <span className="text-sm font-semibold">{v}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* By Month */}
                  {analytics.by_month.length > 0 && (
                    <Card>
                      <CardHeader className="pb-2"><CardTitle className="text-sm">Monthly Trend</CardTitle></CardHeader>
                      <CardContent>
                        <div className="flex items-end gap-2 h-32 overflow-x-auto">
                          {analytics.by_month.map((row) => {
                            const max = Math.max(...analytics.by_month.map(m => m.count));
                            const h = max ? Math.round((row.count / max) * 100) : 0;
                            return (
                              <div key={row.month} className="flex flex-col items-center gap-1 min-w-[40px]">
                                <div className="w-8 bg-[#DC2626] rounded-t" style={{ height: `${h}%` }} title={`${row.count}`} />
                                <span className="text-[10px] text-slate-500">{row.month}</span>
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}
      </Tabs>

      {/* Edit Leave Dialog (HR / Super Admin) */}
      <Dialog open={!!editingLeave} onOpenChange={(o) => !o && setEditingLeave(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Leave Record</DialogTitle>
          </DialogHeader>
          {editingLeave && (
            <form onSubmit={handleEditLeave} className="space-y-3">
              <p className="text-sm text-slate-500">{getEmployeeName(editingLeave.employee_id, editingLeave.employee_name)} ({editingLeave.employee_id || '—'})</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Leave Type</Label>
                  <Select value={editingLeave.leave_type} onValueChange={(v) => setEditingLeave({ ...editingLeave, leave_type: v })}>
                    <SelectTrigger data-testid="edit-leave-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{LEAVE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={editingLeave.status} onValueChange={(v) => setEditingLeave({ ...editingLeave, status: v })}>
                    <SelectTrigger data-testid="edit-leave-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending HOD</SelectItem>
                      <SelectItem value="pending_l2">Pending HR</SelectItem>
                      <SelectItem value="pending_l3">Pending Principal</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Start Date</Label>
                  <Input type="date" value={editingLeave.start_date} onChange={(e) => setEditingLeave({ ...editingLeave, start_date: e.target.value })} data-testid="edit-leave-start" />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input type="date" value={editingLeave.end_date} onChange={(e) => setEditingLeave({ ...editingLeave, end_date: e.target.value })} data-testid="edit-leave-end" />
                </div>
              </div>
              <div>
                <Label>Reason</Label>
                <Textarea value={editingLeave.reason} onChange={(e) => setEditingLeave({ ...editingLeave, reason: e.target.value })} rows={3} data-testid="edit-leave-reason" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setEditingLeave(null)}>Cancel</Button>
                <Button type="submit" className="bg-[#DC2626] hover:bg-[#B91C1C] text-white" data-testid="edit-leave-save">Save Changes</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Approval Flow Info Card */}
      <Card className="bg-slate-50 dark:bg-slate-800/50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
            <div>
              <h4 className="font-medium text-slate-900 dark:text-white">Leave Approval Workflow</h4>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                Leave requests follow a 3-level approval hierarchy: 
                <span className="font-medium"> Employee submits → HOD (L1) → HR (L2) → Principal (L3) → Approved</span>
              </p>
              <div className="flex gap-4 mt-2 text-xs text-slate-500">
                <span>HOD: First approval</span>
                <span>HR: Second approval</span>
                <span>Principal: Final approval</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      {/* HR Approve Confirmation — full or partial (paid + without-pay split) */}
      <Dialog open={!!approveDialog} onOpenChange={() => setApproveDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Leave — {approveDialog?.employee_name}</DialogTitle>
            <DialogDescription>
              {approveDialog?.leave_type} · {approveDialog?.start_date} to {approveDialog?.end_date} · <strong>{approveDialog?.days} day(s)</strong>.
              {isFinalAuthority
                ? ' HR has already verified and set the paid / without-pay split. You only Approve or Reject.'
                : ' Check eligibility before approving. You can approve all days, or pay only some days and mark the rest without pay (as per organisation policy).'}
            </DialogDescription>
          </DialogHeader>
          <>
          {/* Leave balance panel so approvers see sufficiency at a glance (hidden for the
              final authority — HR already decided the split, so it would only confuse). */}
          {!isFinalAuthority && (
          <div className="rounded-md border bg-slate-50 p-3" data-testid="approve-balance-panel">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Employee Leave Balances</p>
            {approveBalances === null ? (
              <p className="text-xs text-slate-400">Loading balances…</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {(approveBalances.some(b => b.leave_type === approveDialog?.leave_type)
                  ? approveBalances
                  : [...approveBalances, { leave_type: approveDialog?.leave_type, balance: 0, total: 0 }]
                ).map((b) => {
                  const isApplied = b.leave_type === approveDialog?.leave_type;
                  const insufficient = isApplied && Number(b.balance || 0) < Number(approveDialog?.days || 0);
                  return (
                    <div key={b.leave_type}
                      className={`px-2 py-1 rounded border text-xs ${isApplied ? (insufficient ? 'border-red-400 bg-red-50' : 'border-emerald-400 bg-emerald-50') : 'border-slate-200 bg-white'}`}
                      data-testid={`approve-bal-${b.leave_type?.replace(/\s+/g, '-')}`}>
                      <span className={isApplied ? 'font-bold' : 'font-medium'}>{b.leave_type}</span>
                      <span className="ml-1.5 font-mono">{b.balance ?? 0}</span>
                      <span className="text-slate-400"> / {b.total ?? 0}</span>
                      {insufficient && <span className="block text-[10px] font-bold text-red-600" data-testid="insufficient-balance-warning">INSUFFICIENT — requested {approveDialog?.days}</span>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          )}
          {/* Comp-off special provision: applied in time but the 1-month validity lapsed */}
          {(() => {
            const isComp = /compensatory|comp[- ]?off/i.test(approveDialog?.leave_type || '');
            if (!isComp || !Array.isArray(compOffCredits)) return null;
            const expiredDays = compOffCredits
              .filter(c => c.is_expired)
              .reduce((s, c) => s + Number(c.remaining_days || 0), 0);
            const liveDays = compOffCredits
              .filter(c => !c.is_expired)
              .reduce((s, c) => s + Number(c.remaining_days || 0), 0);
            const needed = Number(approveDialog?.days || 0);
            if (liveDays >= needed) return null;
            const canSpecial = ['hr', 'super_admin'].includes(user?.role);
            return (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="comp-off-expired-panel">
                <p className="text-xs text-amber-900">
                  <b>Validity lapsed.</b> {liveDays} live day(s) against {needed} requested
                  {expiredDays > 0 && <> · <b>{expiredDays} day(s) expired</b> (1-month validity from the worked date)</>}.
                  {compOffCredits.filter(c => c.is_expired).slice(0, 3).map(c => (
                    <span key={c.credit_id} className="block text-[11px] text-amber-700">
                      Worked {c.worked_on || '—'} · expired {c.expires_at} · {c.remaining_days} day(s) unused
                    </span>
                  ))}
                </p>
                {canSpecial ? (
                  <>
                    <label className="flex items-start gap-2 text-sm cursor-pointer">
                      <input type="checkbox" className="mt-1" checked={specialProvision}
                        onChange={(e) => setSpecialProvision(e.target.checked)}
                        data-testid="special-provision-checkbox" />
                      <span>
                        <b>Approve anyway under special provision</b> — grant the comp-off as it is even though
                        the validity has passed. Recorded in the leave record and the audit log.
                      </span>
                    </label>
                    {specialProvision && (
                      <Input value={specialRemark} onChange={(e) => setSpecialRemark(e.target.value)}
                        placeholder="Reason (e.g. applied in time, approval delayed)"
                        data-testid="special-provision-remark" />
                    )}
                  </>
                ) : (
                  <p className="text-[11px] text-amber-700">
                    Only HR or Super Admin can grant an expired comp-off under the special provision.
                  </p>
                )}
              </div>
            );
          })()}
          <div className="space-y-3 mt-2">
            {isFinalAuthority ? (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900" data-testid="final-approve-note">
                HR has already recorded the paid / without-pay split for this leave. As the final authority
                you only need to <strong>Approve</strong> or <strong>Reject</strong> — the split is kept as HR decided.
              </div>
            ) : (
            <>
            <label className="flex items-center gap-2 text-sm cursor-pointer p-2 rounded border hover:bg-slate-50">
              <input type="radio" name="approve-mode" checked={approveMode === 'full'} onChange={() => setApproveMode('full')} data-testid="approve-mode-full" />
              Approve all <strong>{approveDialog?.days}</strong> day(s) as paid leave
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer p-2 rounded border hover:bg-slate-50">
              <input type="radio" name="approve-mode" checked={approveMode === 'partial'} onChange={() => setApproveMode('partial')} data-testid="approve-mode-partial" />
              Approve partially — some days paid, remaining without pay
            </label>
            <label className="flex items-start gap-2 text-sm cursor-pointer p-2 rounded border border-amber-300 bg-amber-50 hover:bg-amber-100">
              <input type="radio" name="approve-mode" className="mt-1" checked={approveMode === 'lwp'} onChange={() => setApproveMode('lwp')} data-testid="approve-mode-lwp" />
              <span className="text-amber-900">
                <strong>Mark as Leave Without Pay (LWP)</strong>
                <span className="block text-[11px]">
                  All {approveDialog?.days} day(s) are deducted from the monthly attendance / payable
                  days only. The leave balance is NOT touched.
                </span>
              </span>
            </label>
            {approveMode === 'partial' && (
              <div className="pl-6 space-y-1">
                <Label className="text-xs">Paid days (0 – {approveDialog?.days})</Label>
                <Input type="number" min="0" max={approveDialog?.days} step="0.5" value={paidDaysInput}
                  onChange={(e) => setPaidDaysInput(e.target.value)} className="w-32" data-testid="paid-days-input" />
                {paidDaysInput !== '' && !isNaN(parseFloat(paidDaysInput)) && (
                  <p className="text-xs text-amber-600">
                    {parseFloat(paidDaysInput)} day(s) paid · {Math.max((Number(approveDialog?.days) || 0) - parseFloat(paidDaysInput), 0)} day(s) WITHOUT PAY (LOP in payroll)
                  </p>
                )}
              </div>
            )}
            </>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setApproveDialog(null)}>Cancel</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={confirmApprove} data-testid="confirm-approve-btn">
                {isFinalAuthority ? 'Approve' : 'Confirm Approval'}
              </Button>
            </div>
          </div>
          </>
        </DialogContent>
      </Dialog>

      {/* Bulk reject — one common reason for the selected lot */}
      <Dialog open={bulkReject.open} onOpenChange={o => !o && setBulkReject({ open: false, reason: '' })}>
        <DialogContent className="max-w-md" data-testid="bulk-reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject {selectedIds.length} leave request(s)</DialogTitle>
            <DialogDescription>
              The same reason is recorded on each request. Staff and HR both see it and can apply again.
            </DialogDescription>
          </DialogHeader>
          <Textarea rows={3} placeholder="Reason for rejection" value={bulkReject.reason}
            onChange={e => setBulkReject(b => ({ ...b, reason: e.target.value }))}
            data-testid="bulk-reject-reason" />
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => setBulkReject({ open: false, reason: '' })}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" disabled={bulkBusy}
              onClick={() => {
                if (!bulkReject.reason.trim()) { toast.error('Please write the reason for rejection'); return; }
                doBulk('reject', bulkReject.reason.trim());
              }} data-testid="bulk-reject-confirm">
              Reject Selected
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject with a reason — mandatory for the Principal / Director */}
      <Dialog open={!!rejectDialog} onOpenChange={() => setRejectDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Leave — {rejectDialog?.employee_name}</DialogTitle>
            <DialogDescription>
              {rejectDialog?.leave_type} · {rejectDialog?.start_date} to {rejectDialog?.end_date} · <strong>{rejectDialog?.days} day(s)</strong>.
              The staff member and HR will both see this reason and can apply again.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Reason for rejection {isFinalAuthority && <span className="text-red-600">*</span>}</Label>
            <Textarea value={rejectRemark} onChange={(e) => setRejectRemark(e.target.value)} rows={3}
              placeholder="e.g. dates clash with exam duty — re-apply for a different date"
              data-testid="reject-remark-input" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => setRejectDialog(null)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={confirmReject} data-testid="confirm-reject-btn">
              Reject
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
